import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, CalendarCheck, CameraOff, EyeOff, GraduationCap, RefreshCw, Timer, UserMinus, Users } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  ProgressBar,
  SegmentedControl,
  Select,
  Spinner,
  StatCard,
} from '../../Components/ui';
import { isCanceledRequest } from '../../Components/Students/examData';
import { formatServerDateTime } from '../../utils/serverDate';
import { DailyBars, HourHeatmap, TimeScoreScatter } from './ActivityCharts';
import { fetchCourseReport, fetchCourses } from './activityApi';
import {
  EMPTY,
  formatDay,
  formatDuration,
  formatInactiveDays,
  formatVideoRange,
  lessonCode,
  toMinutes,
} from './activityFormat';
import { DAY_OPTIONS, useReport } from './useReport';
import './LearningActivity.css';

// Backend'dagi `management/analytics/service.py: HOTSPOT_BUCKET_SEC`
const HOTSPOT_BUCKET_SEC = 30;

const percentOf = (part, total) => (total ? Math.round((part * 100) / total) : 0);

function LessonsFunnel({ lessons, learnersTotal }) {
  return (
    <Card
      title="Darslar bo'yicha"
      subtitle="Nechta talaba boshlagan va videoni 90%+ ko'rib tugatgan — qayerda tashlab ketishyapti"
      padding="none"
    >
      <div className="ui-table-wrap ui-la-table">
        <table className="ui-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Dars</th>
              <th className="ui-table-cell--numeric">Boshlagan</th>
              <th>Tugatgan</th>
              <th className="ui-table-cell--numeric">O'rtacha ko'rilgan</th>
              <th className="ui-table-cell--numeric">Qayta ko'rish</th>
              <th className="ui-table-cell--numeric">Tab pauza</th>
              <th className="ui-table-cell--numeric">AI savol</th>
              <th className="ui-table-cell--numeric" title="Kamera nazorati: yuz kadrda bo'lmagani uchun video to'xtagan">Turib ketdi</th>
              <th className="ui-table-cell--numeric" title="Kamera nazorati: boshqa tomonga qaragani uchun video to'xtagan">Chalg'idi</th>
            </tr>
          </thead>
          <tbody>
            {lessons.map((row) => (
              <tr key={row.lesson_id}>
                <td className="ui-la-table__code">{lessonCode(row)}</td>
                <td>
                  <span className="ui-la-table__title" title={row.title}>{row.title}</span>
                </td>
                <td className="ui-table-cell--numeric">{row.started}</td>
                <td>
                  <div className="ui-la-table__funnel">
                    <ProgressBar
                      value={percentOf(row.completed, learnersTotal)}
                      tone="success"
                      label={`${row.title}: tugatganlar`}
                      className="ui-la-table__progress"
                    />
                    <span className="ui-la-table__count">{row.completed}</span>
                  </div>
                </td>
                <td className="ui-table-cell--numeric">
                  {row.avg_watched_percent === null ? EMPTY : `${row.avg_watched_percent}%`}
                </td>
                <td className="ui-table-cell--numeric">{row.rewinds || EMPTY}</td>
                <td className="ui-table-cell--numeric">{row.tab_hidden || EMPTY}</td>
                <td className="ui-table-cell--numeric">{row.ai_questions || EMPTY}</td>
                <td className="ui-table-cell--numeric">{row.away_count || EMPTY}</td>
                <td className="ui-table-cell--numeric">{row.look_away_count || EMPTY}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Videoning 30 soniyalik bo'laklari bo'yicha hodisalar eng ko'p to'plangan joylar
function Hotspots({ hotspots, title, subtitle, empty, countLabel }) {
  return (
    <Card title={title} subtitle={subtitle} padding={hotspots.length ? 'none' : 'md'}>
      {hotspots.length === 0 ? (
        <p className="ui-la-muted">{empty}</p>
      ) : (
        <div className="ui-table-wrap ui-la-table">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Dars</th>
                <th>Videodagi joy</th>
                <th className="ui-table-cell--numeric">{countLabel}</th>
              </tr>
            </thead>
            <tbody>
              {hotspots.map((row) => (
                <tr key={`${row.lesson_id}-${row.position_sec}`}>
                  <td>
                    <span className="ui-la-table__title" title={row.title}>
                      {lessonCode(row)} · {row.title}
                    </span>
                  </td>
                  <td>{formatVideoRange(row.position_sec, HOTSPOT_BUCKET_SEC)}</td>
                  <td className="ui-table-cell--numeric">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AtRisk({ rows, onOpen }) {
  return (
    <Card
      title="Xavf guruhi"
      subtitle="Kursni tugatmagan va 7 kundan beri video ko'rmagan (yoki umuman boshlamagan) talabalar"
      padding={rows.length ? 'none' : 'md'}
    >
      {rows.length === 0 ? (
        <p className="ui-la-muted">Xavf guruhida talaba yo'q</p>
      ) : (
        <div className="ui-table-wrap ui-la-table">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Talaba</th>
                <th>Progress</th>
                <th>So'nggi faollik</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.learner_id} className="ui-table-row--clickable" onClick={() => onOpen(row.learner_id)}>
                  <td>{row.name}</td>
                  <td>
                    <ProgressBar value={row.progress} showValue label={`${row.name}: progress`} className="ui-la-table__progress" />
                  </td>
                  <td title={formatServerDateTime(row.last_activity_at) || undefined}>
                    {formatInactiveDays(row.inactive_days)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// /learning-analytics?courseId=… — kurs bo'yicha o'qish faolligi
const CourseActivity = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  const [courses, setCourses] = useState([]);
  const [days, setDays] = useState(30);
  const [reload, setReload] = useState(0);
  const { status, data } = useReport(fetchCourseReport, courseId, days, reload);

  useEffect(() => {
    const controller = new AbortController();
    fetchCourses(controller.signal)
      .then(setCourses)
      .catch((err) => {
        if (!isCanceledRequest(err)) console.log(err);
      });
    return () => controller.abort();
  }, []);

  // Kurs tanlanmagan bo'lsa birinchisi ochiladi
  useEffect(() => {
    if (!courseId && courses.length) setSearchParams({ courseId: String(courses[0].id) }, { replace: true });
  }, [courseId, courses, setSearchParams]);

  const openLearner = (learnerId) => navigate(`/learning-activity?learnerId=${learnerId}`);

  const summary = data?.summary;
  const daily = (data?.daily || []).map((row) => ({ ...row, minutes: toMinutes(row.seconds) }));
  const scatter = (data?.learners || [])
    .filter((row) => row.avg_score !== null)
    .map((row) => ({ ...row, seconds: row.total_seconds, hours: Math.round(row.total_seconds / 360) / 10, score: row.avg_score }));

  return (
    <div className="ui-la">
      <PageHeader
        title="O'qish analitikasi"
        subtitle={
          data?.tracking_since
            ? `Video ko'rish ma'lumotlari ${formatDay(data.tracking_since, { withYear: true })} dan beri yig'ilmoqda`
            : "Talabalar videodarslarni qanday ko'rayotgani"
        }
        actions={
          <>
            <Select
              aria-label="Kurs"
              value={courseId || ''}
              onChange={(e) => setSearchParams({ courseId: e.target.value }, { replace: true })}
              wrapperClassName="ui-la__course"
            >
              {!courseId && <option value="">Kursni tanlang</option>}
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </Select>
            <SegmentedControl aria-label="Davr" size="sm" options={DAY_OPTIONS} value={days} onChange={setDays} />
            <Button variant="secondary" size="sm" leftIcon={<RefreshCw />} onClick={() => setReload((x) => x + 1)} disabled={!courseId}>
              Yangilash
            </Button>
          </>
        }
      />

      {!data && status === 'loading' && (
        <div className="ui-la-loading">
          <Spinner size="lg" label="Yuklanmoqda" />
        </div>
      )}

      {!data && status === 'error' && (
        <Card padding="none">
          <EmptyState
            title="Ma'lumotni yuklab bo'lmadi"
            action={<Button variant="secondary" onClick={() => setReload((x) => x + 1)}>Qayta urinish</Button>}
          />
        </Card>
      )}

      {!courseId && status === 'idle' && (
        <Card padding="none">
          <EmptyState title="Kurs tanlanmagan" description="Yuqoridan kursni tanlang" />
        </Card>
      )}

      {data && (
        <div className={`ui-la__body${status === 'loading' ? ' ui-la__body--refreshing' : ''}`}>
          <div className="ui-la__kpis">
            <StatCard icon={<Users />} tone="neutral" label="Kursdagi talabalar" value={summary.learners_total} />
            <StatCard icon={<Activity />} tone="success" label="Bugun o'qiganlar" value={summary.active_today} />
            <StatCard icon={<CalendarCheck />} tone="info" label="7 kunda o'qiganlar" value={summary.active_7_days} />
            <StatCard icon={<AlertTriangle />} tone="brand" label="Xavf guruhida" value={summary.at_risk} />
            <StatCard icon={<UserMinus />} tone="neutral" label="Hali ko'rmagan" value={summary.never_started} />
            <StatCard icon={<GraduationCap />} tone="success" label="Tugatganlar" value={summary.finished} />
            <StatCard
              icon={<Timer />}
              tone="info"
              label={`Faol talabaga o'rtacha (${days} kun)`}
              value={formatDuration(summary.avg_seconds_per_active_learner)}
            />
            <StatCard
              icon={<EyeOff />}
              tone="warning"
              label="Chalg'ish, har soat ko'rishga"
              value={summary.distractions_per_hour === null ? EMPTY : summary.distractions_per_hour}
              title={`${days} kunda ${summary.distractions} marta: turib ketish yoki boshqa tomonga qarash`}
            />
            <StatCard
              icon={<CameraOff />}
              tone="neutral"
              label="Kamerasiz ko'rganlar"
              value={summary.camera_off_learners}
              title="Davr ichida kamera ruxsati berilmagan yoki kamera ishlamagan holda dars ko'rgan talabalar"
            />
          </div>

          <div className="ui-la__grid-2">
            <Card title="Kunlik o'qish vaqti" subtitle="Barcha talabalar, daqiqa">
              <DailyBars
                data={daily}
                valueKey="minutes"
                label="Kunlik o'qish vaqti"
                tooltip={(row) => (
                  <>
                    <b>{formatDuration(row.seconds)}</b>
                    <span>{formatDay(row.date, { withYear: true })}</span>
                  </>
                )}
              />
            </Card>
            <Card title="Kunlik faol talabalar" subtitle="Kuniga kamida 1 daqiqa video ko'rganlar">
              <DailyBars
                data={daily}
                valueKey="active_learners"
                label="Kunlik faol talabalar"
                tooltip={(row) => (
                  <>
                    <b>{row.active_learners} talaba</b>
                    <span>{formatDay(row.date, { withYear: true })}</span>
                  </>
                )}
              />
            </Card>
          </div>

          <div className="ui-la__grid-2">
            <Card title="Qaysi kun va soatlarda o'qishadi" subtitle={`Oxirgi ${days} kun, Toshkent vaqti`}>
              <HourHeatmap cells={data.heatmap} />
            </Card>
            <Card title="Ko'rilgan vaqt va imtihon balli" subtitle="Har nuqta — bitta talaba: gorizontal — jami ko'rilgan soat, vertikal — o'rtacha ball">
              {scatter.length < 2 ? (
                <p className="ui-la-muted">Taqqoslash uchun imtihon topshirgan talabalar yetarli emas</p>
              ) : (
                <TimeScoreScatter data={scatter} />
              )}
            </Card>
          </div>

          <LessonsFunnel lessons={data.lessons} learnersTotal={summary.learners_total} />

          <div className="ui-la__grid-2">
            <Hotspots
              hotspots={data.hotspots}
              title="Eng ko'p qayta ko'rilgan joylar"
              subtitle="Talabalar orqaga qaytib qayta ko'rgan joylar — tushunarsiz bo'lishi mumkin"
              empty="Hali orqaga qaytishlar yo'q"
              countLabel="Qayta ko'rishlar"
            />
            <Hotspots
              hotspots={data.distraction_hotspots}
              title="Ko'pchilik qayerda chalg'iydi"
              subtitle="Kamera nazorati videoni to'xtatgan joylar — zerikarli yoki juda uzun bo'lishi mumkin"
              empty="Hali kamera nazorati videoni to'xtatmagan"
              countLabel="To'xtashlar"
            />
          </div>

          <AtRisk rows={data.at_risk} onOpen={openLearner} />
        </div>
      )}
    </div>
  );
};

export default CourseActivity;
